import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, KeyRound, Pencil, Ban, CheckCircle2, ShieldCheck, Lock, Percent } from "lucide-react";

const ROLES = ["super_admin", "admin", "doctor", "reception", "nurse", "lab", "pharmacy", "billing", "accounts", "hr", "manager"];
// Narrow extra capabilities an admin can quick-grant per user — key = user-visible
// label, value = {module, action, color, help}. These are also surfaced in the
// Custom-permissions dialog's extras row.
const QUICK_GRANTS = [
  {
    label: "Discount Request",
    icon: Percent,
    module: "billing",
    action: "request_discount",
    activeClass: "bg-emerald-600 text-white border-emerald-700",
    inactiveClass: "border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-700",
    help: "Lets this user raise a discount request from OPD billing (requires admin approval before applying).",
    // Only sensible to grant on roles that normally lack billing.create — admins
    // already have both.
    showForRoles: ["doctor", "nurse", "reception", "lab", "pharmacy", "manager"],
  },
];
const emptyUser = { email: "", password: "", name: "", role: "reception", mobile: "", designation: "", department: "", status: "active" };

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [permOpen, setPermOpen] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [customGrants, setCustomGrants] = useState([]);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => toast.error("Forbidden"));
    api.get("/permissions/matrix").then((r) => setMatrix(r.data)).catch(() => {});
    api.get("/doctors").then((r) => setDoctors(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        const patch = { ...form }; delete patch.password; delete patch.email;
        await api.put(`/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password) return toast.error("Email and password required");
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); setEditing(null); setForm(emptyUser); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    await api.put(`/users/${u.id}`, { status: next });
    toast.success(`${u.name} → ${next}`);
    load();
  };

  const startPwReset = (u) => { setPwTarget(u); setNewPw(""); setPwOpen(true); };
  const submitPw = async () => {
    if (!newPw || newPw.length < 6) return toast.error("Min 6 chars");
    await api.post(`/users/${pwTarget.id}/reset-password`, { password: newPw });
    toast.success("Password reset");
    setPwOpen(false);
  };

  const startEdit = (u) => { setEditing(u); setForm({ ...emptyUser, ...u, password: "" }); setOpen(true); };
  const openPerm = (u) => { setPermTarget(u); setCustomGrants(u.custom_permissions || []); setPermOpen(true); };

  // Quick-grant toggle used on each user row (one-click grant/revoke without
  // opening the full permission matrix). We mutate the user's
  // ``custom_permissions`` list in-place to avoid clobbering any other extras
  // the admin may have granted earlier.
  const toggleQuickGrant = async (u, grant) => {
    const current = Array.isArray(u.custom_permissions) ? u.custom_permissions : [];
    const has = current.some(p => p.module === grant.module && p.action === grant.action);
    const next = has
      ? current.filter(p => !(p.module === grant.module && p.action === grant.action))
      : [...current, { module: grant.module, action: grant.action }];
    try {
      await api.put(`/users/${u.id}`, { custom_permissions: next });
      toast.success(has ? `Revoked ${grant.label} from ${u.name}` : `${grant.label} granted to ${u.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update permission");
    }
  };
  const togglePerm = (module, action) => {
    const exists = customGrants.find((p) => p.module === module && p.action === action);
    if (exists) setCustomGrants(customGrants.filter((p) => !(p.module === module && p.action === action)));
    else setCustomGrants([...customGrants, { module, action }]);
  };
  const savePerms = async () => {
    await api.put(`/users/${permTarget.id}`, { custom_permissions: customGrants });
    toast.success("Custom permissions updated");
    setPermOpen(false); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">User Management</h1>
          <p className="text-sm text-slate-500">Create, edit and permission users. Backend enforces every action.</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => { setEditing(null); setForm(emptyUser); setOpen(true); }} className="bg-blue-700 hover:bg-blue-800 rounded-sm"><UserPlus className="w-4 h-4 mr-1"/> Add User</Button>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-white border">
          <TabsTrigger value="users" data-testid="um-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="um-tab-matrix">Role Permission Matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="bg-white border">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Name</th><th className="label-tiny">Email</th><th className="label-tiny">Role</th><th className="label-tiny">Department</th><th className="label-tiny">Quick Grants</th><th className="label-tiny">Status</th><th className="label-tiny w-56">Actions</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b">
                    <td className="p-2 font-medium">{u.name}</td>
                    <td className="font-mono text-xs">{u.email}</td>
                    <td><Badge className="rounded-sm text-[10px] bg-blue-100 text-blue-800">{u.role}</Badge></td>
                    <td className="text-xs">{u.department || "-"}</td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {QUICK_GRANTS
                          .filter(g => !g.showForRoles || g.showForRoles.includes(u.role))
                          .map((g) => {
                            const granted = (u.custom_permissions || []).some(p => p.module === g.module && p.action === g.action);
                            const Icon = g.icon;
                            return (
                              <button
                                key={`${u.id}-${g.module}-${g.action}`}
                                onClick={() => toggleQuickGrant(u, g)}
                                data-testid={`qg-${g.action}-${u.email}`}
                                title={g.help}
                                className={`px-2 py-0.5 text-[10px] rounded-sm border flex items-center gap-1 transition ${granted ? g.activeClass : g.inactiveClass}`}
                              >
                                {Icon && <Icon className="w-3 h-3" />}
                                {granted ? "✓ " : "+ "}{g.label}
                              </button>
                            );
                          })}
                        {QUICK_GRANTS.filter(g => !g.showForRoles || g.showForRoles.includes(u.role)).length === 0 && (
                          <span className="text-[10px] text-slate-400 italic">—</span>
                        )}
                      </div>
                    </td>
                    <td><Badge className={`rounded-sm text-[10px] ${u.status === "active" ? "bg-teal-100 text-teal-800" : u.status === "suspended" ? "bg-red-100 text-red-800" : "bg-slate-100"}`}>{u.status}</Badge></td>
                    <td className="flex gap-1 py-2">
                      <button onClick={() => startEdit(u)} data-testid={`edit-user-${u.email}`} className="p-1 border rounded-sm hover:bg-slate-100" title="Edit"><Pencil className="w-3 h-3"/></button>
                      <button onClick={() => openPerm(u)} className="p-1 border border-blue-300 text-blue-700 rounded-sm hover:bg-blue-50" title="Custom permissions"><ShieldCheck className="w-3 h-3"/></button>
                      <button onClick={() => startPwReset(u)} className="p-1 border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50" title="Reset password"><KeyRound className="w-3 h-3"/></button>
                      <button onClick={async () => {
                        const pin = window.prompt(`Set Kiosk PIN for ${u.name}?\n4-6 digits · leave blank to clear`, "");
                        if (pin === null) return;
                        try { await api.put(`/users/${u.id}/kiosk-pin`, { pin: pin.trim() }); toast.success(pin ? "PIN set" : "PIN cleared"); }
                        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} data-testid={`user-kiosk-pin-${u.email}`} className="p-1 border border-blue-300 text-blue-700 rounded-sm hover:bg-blue-50" title="Set Kiosk PIN"><Lock className="w-3 h-3"/></button>
                      <button onClick={() => toggleStatus(u)} className={`p-1 border rounded-sm ${u.status === "active" ? "border-red-300 text-red-700 hover:bg-red-50" : "border-teal-300 text-teal-700 hover:bg-teal-50"}`} title="Toggle status">{u.status === "active" ? <Ban className="w-3 h-3"/> : <CheckCircle2 className="w-3 h-3"/>}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="matrix">
          {matrix && (
            <div className="bg-white border overflow-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="p-2 text-left label-tiny sticky left-0 bg-slate-50">Module</th>
                    {ROLES.map((r) => <th key={r} className="label-tiny text-center px-2 py-1">{r.replace("_", " ")}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {matrix.modules.map((m) => (
                    <tr key={m} className="border-b">
                      <td className="p-2 font-medium sticky left-0 bg-white">{m}</td>
                      {ROLES.map((r) => {
                        const acts = (matrix.role_defaults[r] || {})[m] || [];
                        return (
                          <td key={r} className="text-center py-1">
                            {acts.length > 0 ? (
                              <div className="flex justify-center gap-0.5">
                                {matrix.actions.map((a) => (
                                  <span key={a} title={a} className={`w-4 h-4 flex items-center justify-center text-[8px] font-mono ${acts.includes(a) ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-400"}`}>{a[0].toUpperCase()}</span>
                                ))}
                              </div>
                            ) : <span className="text-slate-300">·</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 text-[11px] text-slate-500 border-t">Legend — V: View · C: Create · E: Edit · D: Delete · P: Print · X: Export · A: Approve. Backend enforces every action; sidebar & buttons render from this matrix.</div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* User form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit — ${editing.name}` : "Add New User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-tiny">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 mt-1" data-testid="u-name"/></div>
            <div><Label className="label-tiny">Email</Label><Input value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 mt-1" data-testid="u-email"/></div>
            {!editing && <div><Label className="label-tiny">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-9 mt-1" data-testid="u-pw"/></div>}
            <div><Label className="label-tiny">Role</Label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-9 border px-2 text-sm mt-1" data-testid="u-role">{ROLES.map((r) => <option key={r}>{r}</option>)}</select></div>
            <div><Label className="label-tiny">Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-9 mt-1"/></div>
            <div><Label className="label-tiny">Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="h-9 mt-1"/></div>
            <div><Label className="label-tiny">Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="h-9 mt-1"/></div>
            <div><Label className="label-tiny">Status</Label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 border px-2 text-sm mt-1"><option>active</option><option>inactive</option><option>suspended</option></select></div>
            {form.role === "doctor" && (
              <div className="col-span-2"><Label className="label-tiny">Link to Doctor Profile (for data scoping)</Label>
                <select value={form.doctor_id || ""} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })} className="w-full h-9 border px-2 text-sm mt-1">
                  <option value="">-- auto-match by name --</option>
                  {doctors.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.department}</option>)}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">Doctor sees only OPDs / IPDs assigned to this doctor profile.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="u-save" className="bg-blue-700 hover:bg-blue-800">{editing ? "Save" : "Create User"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password · {pwTarget?.name}</DialogTitle></DialogHeader>
          <Input type="password" placeholder="New password (min 6 chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="pw-input"/>
          <DialogFooter><Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button><Button onClick={submitPw} className="bg-amber-700 hover:bg-amber-800">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom permissions grid */}
      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Custom Permissions · {permTarget?.name} ({permTarget?.role})</DialogTitle></DialogHeader>
          {matrix && (
            <div className="max-h-96 overflow-auto border">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 sticky top-0"><th className="p-2 text-left label-tiny">Module</th>{matrix.actions.map((a) => <th key={a} className="label-tiny text-center px-2">{a}</th>)}</tr></thead>
                <tbody>
                  {matrix.modules.map((m) => (
                    <tr key={m} className="border-b">
                      <td className="p-2 font-medium">{m}</td>
                      {matrix.actions.map((a) => {
                        const roleHas = (matrix.role_defaults[permTarget?.role] || {})[m]?.includes(a);
                        const custom = customGrants.find((p) => p.module === m && p.action === a);
                        return (
                          <td key={a} className="text-center">
                            <button onClick={() => togglePerm(m, a)} className={`w-5 h-5 text-[9px] font-mono border ${roleHas ? "bg-teal-600 text-white border-teal-700" : custom ? "bg-blue-600 text-white border-blue-700" : "bg-white border-slate-300"}`}>{roleHas || custom ? "✓" : ""}</button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[11px] text-slate-500">Green = role default, Blue = extra grant, Empty = no access</div>
          <DialogFooter><Button variant="outline" onClick={() => setPermOpen(false)}>Cancel</Button><Button onClick={savePerms} className="bg-blue-700 hover:bg-blue-800">Save Custom Permissions</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
